import { MaterialComponent } from './material-component'
import { JsonMap } from '../../work-orders/work-order'
import { ComparatorBuilder, Quantity } from '@elderbyte/ngx-starter'

export enum BomItemType {
  /**
   * The item references a material (stock)
   */
  Material = 'Material',

  /**
   * The item references a non tracked material
   */
  NonStock = 'NonStock',

  /**
   * The item references an attachment.
   *  - The mime type should be used.
   *  - The data field contains an URI (URL or URN for supported systems) to the document
   */
  Attachment = 'Attachment',

  /**
   * Only the description is used, other fields are undefined.
   */
  Text = 'Text',

  /**
   * This item holds embedded configuration data in json.
   */
  Config = 'Config',

  /**
   * This item holds generic information in key value pairs.
   */
  PropertySet = 'PropertySet',

  /**
   * Item for intra materials which are of abstract nature
   */
  Phantom = 'Phantom',
}

export class JsonDocument<T = any> {
  public type: string
  public data: T
}

export class AttachmentRef {
  public uri: string
  public mimeType: string
}

export class PropertySetValue {
  public primitive: object
  public quantity: Quantity
}

export class PropertySet {
  public schema: string
  public properties: JsonMap<PropertySetValue>
}

export abstract class BomNode<T extends BomNode<T>> {
  /**
   * Sub items of this bom node
   */
  public items: JsonMap<T>
  public namedItems: NamedBomComponent[]
}

export class NamedBomComponent {
  public static readonly NameComparer = ComparatorBuilder.fieldSort('name')

  public static of(name: string, component: BomComponent): NamedBomComponent {
    return new NamedBomComponent(name, component)
  }

  constructor(
    public readonly name: string,
    public readonly component: BomComponent
  ) {}
}

export class BomComponent extends BomNode<BomComponent> {
  /**
   * The BOM Item type
   */
  public type: BomItemType

  /**
   * The component description
   */
  public description: string

  public config: JsonDocument

  public document: AttachmentRef

  public propertySet: PropertySet

  public material: MaterialComponent
}

export class Bom extends BomNode<BomComponent> {
  public static of(...component: NamedBomComponent[]): Bom {
    const bom = new Bom()
    bom.namedItems = component
    bom.items = {}
    component.forEach((nc) => (bom.items[nc.name] = nc.component))
    return bom
  }

  public static populateNamed(node: BomNode<BomComponent>): BomNode<BomComponent> {
    if (node) {
      node.namedItems = Bom.toNamedComponents(node.items)
      node.namedItems.forEach((cmp) => Bom.populateNamed(cmp.component))
    }
    return node
  }

  public static toNamedComponents(items: JsonMap<BomComponent>): NamedBomComponent[] {
    return Object.keys(items)
      .map((k) => new NamedBomComponent(k, items[k]))
      .sort(NamedBomComponent.NameComparer)
  }
}
